import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Download, ShieldCheck } from "lucide-react";

const STATUS_STYLES = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function StatusBadge({ status }) {
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
            {status}
        </span>
    );
}

export default function Privacy() {
    const [requests, setRequests] = useState([]);
    const [retention, setRetention] = useState(null);
    const [history, setHistory] = useState({ summary: {}, items: [] });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ request_type: "deletion", details: "" });
    const [controller, setController] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/privacy/requests");
            const retentionRes = await api.get("/privacy/retention");
            const historyRes = await api.get("/privacy/history");
            // Any authenticated user can read this — it is the same designation the estate's
            // director set on the admin privacy page, shown here so a resident can actually
            // find out who it is without asking.
            const settingsRes = await api.get("/estate/settings");
            setRequests(res.data || []);
            setRetention(retentionRes.data);
            setHistory(historyRes.data || { summary: {}, items: [] });
            setController({
                type: settingsRes.data?.controller_type || "rmc",
                name: settingsRes.data?.controller_organisation_name || null,
                contact: settingsRes.data?.controller_contact || null,
            });
        } catch {
            toast.error("Failed to load privacy requests");
        } finally {
            setLoading(false);
        }
    };

    const exportFiles = async () => {
        try {
            const res = await api.get("/privacy/export/files", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `estate-self-help-files-${new Date().toISOString().slice(0, 10)}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Uploaded file archive downloaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not export uploaded files");
        }
    };

    useEffect(() => { load(); }, []);

    const exportData = async () => {
        try {
            const res = await api.get("/privacy/export");
            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `estate-self-help-export-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Personal data export downloaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not export your data");
        }
    };

    const submitRequest = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/privacy/requests", form);
            toast.success("Privacy request submitted");
            setForm({ request_type: "deletion", details: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit privacy request");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 max-w-4xl" data-testid="privacy-page">
            <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
                    <ShieldCheck size={14} /> Privacy
                </div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Your data rights</h1>
                <p className="text-sm text-slate-500 mt-1">
                    Export your resident records or ask the estate admin to review deletion, restriction, or correction requests.
                </p>
            </div>

            {controller && (
                <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="controller-notice">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Who is responsible for your data</div>
                    {controller.type === "agency" && controller.name ? (
                        <p className="text-sm text-slate-700 mt-2">
                            Your management company has designated <strong>{controller.name}</strong> to act as
                            data controller on its behalf.
                            {controller.contact && <> Contact: <strong>{controller.contact}</strong>.</>}
                        </p>
                    ) : (
                        <p className="text-sm text-slate-700 mt-2">
                            Your management company is the data controller for your resident data. This product
                            acts as its processor — it does not control your data itself.
                        </p>
                    )}
                </section>
            )}

            <section className="border border-slate-200 rounded-lg bg-white p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">Data export</div>
                        <p className="text-sm text-slate-600 mt-2">
                            Download a JSON copy of your profile, cases, timeline events, document metadata, appeal drafts,
                            audit entries, and privacy requests.
                        </p>
                    </div>
                    <Button onClick={exportData} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="privacy-export-button">
                        <Download size={16} className="mr-2" /> Export my data
                    </Button>
                    <Button onClick={exportFiles} variant="outline" data-testid="privacy-file-export-button">
                        <Download size={16} className="mr-2" /> Export uploaded files
                    </Button>
                </div>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="privacy-retention-section">
                <div className="text-xs uppercase tracking-widest text-slate-500">Retention summary</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                    <div className="border border-slate-200 rounded-md p-3">
                        <div className="text-xs uppercase tracking-widest text-slate-500">Cases</div>
                        <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{retention?.cases?.length || 0}</div>
                    </div>
                    <div className="border border-slate-200 rounded-md p-3">
                        <div className="text-xs uppercase tracking-widest text-slate-500">Documents</div>
                        <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{retention?.documents?.length || 0}</div>
                    </div>
                    <div className="border border-slate-200 rounded-md p-3">
                        <div className="text-xs uppercase tracking-widest text-slate-500">Policy</div>
                        <div className="text-sm text-slate-700 mt-1">Review on deletion/restriction request</div>
                    </div>
                </div>
                <p className="text-sm text-slate-600 mt-4">
                    {retention?.policy?.case_records || "Case records are retained while needed for estate issue management and audit."}
                </p>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="privacy-history-section">
                <div className="px-4 py-3 border-b border-slate-200">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Sharing and access history</div>
                    <p className="text-sm text-slate-600 mt-2">
                        Review when your cases or documents were shared, accessed by admins, downloaded by non-owners, or reviewed for retention.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
                        <div className="border border-slate-200 rounded-md p-3">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Total</div>
                            <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{history.summary?.total || 0}</div>
                        </div>
                        <div className="border border-slate-200 rounded-md p-3">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Sharing</div>
                            <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{history.summary?.sharing_events || 0}</div>
                        </div>
                        <div className="border border-slate-200 rounded-md p-3">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Access</div>
                            <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{history.summary?.admin_or_external_access || 0}</div>
                        </div>
                        <div className="border border-slate-200 rounded-md p-3">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Retention</div>
                            <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{history.summary?.retention_reviews || 0}</div>
                        </div>
                    </div>
                </div>
                <table className="w-full text-sm" data-testid="privacy-history-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">When</th>
                            <th className="table-header-cell">Action</th>
                            <th className="table-header-cell">Record</th>
                            <th className="table-header-cell">Actor</th>
                            <th className="table-header-cell">Visibility</th>
                            <th className="table-header-cell">Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="table-cell-tight text-slate-500">Loading...</td></tr>}
                        {!loading && (history.items || []).length === 0 && <tr><td colSpan={6} className="table-cell-tight text-slate-500">No sharing or access history yet.</td></tr>}
                        {(history.items || []).map((item) => (
                            <tr key={item.id} className="border-b border-slate-100" data-testid={`privacy-history-${item.id}`}>
                                <td className="table-cell-tight text-slate-500">{item.timestamp ? new Date(item.timestamp).toLocaleString() : "-"}</td>
                                <td className="table-cell-tight text-slate-900">{item.action.replaceAll("_", " ")}</td>
                                <td className="table-cell-tight text-slate-700">
                                    {item.title || item.resource_id}
                                    <div className="text-xs text-slate-500">{item.resource_type}</div>
                                </td>
                                <td className="table-cell-tight text-slate-700">{item.actor_email || "-"}</td>
                                <td className="table-cell-tight text-slate-700">{item.visibility || "-"}</td>
                                <td className="table-cell-tight text-slate-700 max-w-xs">{item.access_reason || item.metadata?.access_reason || item.metadata?.reason || "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white p-5">
                <div className="text-xs uppercase tracking-widest text-slate-500">Submit request</div>
                <form onSubmit={submitRequest} className="space-y-4 mt-4" data-testid="privacy-request-form">
                    <div className="space-y-1.5">
                        <Label>Request type</Label>
                        <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                            <SelectTrigger data-testid="privacy-request-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="deletion">Deletion request</SelectItem>
                                <SelectItem value="restriction">Restrict processing</SelectItem>
                                <SelectItem value="correction">Correct inaccurate data</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Details</Label>
                        <Textarea
                            rows={4}
                            value={form.details}
                            onChange={(e) => setForm({ ...form, details: e.target.value })}
                            placeholder="Explain what you want reviewed. For deletion, mention whether you also want uploaded private documents removed."
                            required
                            data-testid="privacy-request-details-input"
                        />
                    </div>
                    <Button type="submit" disabled={submitting} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="privacy-request-submit-button">
                        {submitting ? "Submitting..." : "Submit request"}
                    </Button>
                </form>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
                <div className="px-3 py-2 text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">My requests</div>
                <table className="w-full text-sm" data-testid="privacy-requests-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Type</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Details</th>
                            <th className="table-header-cell">Admin response</th>
                            <th className="table-header-cell">Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} className="table-cell-tight text-slate-500">Loading...</td></tr>}
                        {!loading && requests.length === 0 && <tr><td colSpan={5} className="table-cell-tight text-slate-500">No privacy requests yet.</td></tr>}
                        {requests.map((req) => (
                            <tr key={req.id} className="border-b border-slate-100" data-testid={`privacy-request-${req.id}`}>
                                <td className="table-cell-tight text-slate-900 capitalize">{req.request_type}</td>
                                <td className="table-cell-tight"><StatusBadge status={req.status} /></td>
                                <td className="table-cell-tight text-slate-700 max-w-xs">{req.details}</td>
                                <td className="table-cell-tight text-slate-700 max-w-xs">{req.admin_response || "-"}</td>
                                <td className="table-cell-tight text-slate-500">{new Date(req.created_at).toLocaleDateString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
